import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HubContext,
  mapQuotesToSubmissions,
  Submissions,
  transformHubContextToAcceptedQuote,
} from '../../types/hub';
import FormattedCurrency from '../common/FormattedCurrency';
import CollapsibleContent from '../common/CollapsibleContent';
import ProfileAvatar from '../common/ProfileAvatar';
import BidderDetailsModal from '../quotes/BidderDetailsModal';

interface AcceptedOfferSectionProps {
  hub: HubContext;
}

export const AcceptedOfferSection: React.FC<AcceptedOfferSectionProps> = ({ hub }) => {
  const submissions: Submissions = mapQuotesToSubmissions(hub.job.quotes);
  const { t } = useTranslation('hub');

  const [showDetails, setShowDetails] = useState(false);

  const acceptedSubmission = submissions.find(sub => sub.status === 'accepted');

  const grandTotal = acceptedSubmission
    ? acceptedSubmission.totalAmount +
      (acceptedSubmission.teamMembers
        ? acceptedSubmission.teamMembers.reduce(
            (sum, member) =>
              sum + member.tasks.reduce((taskSum, task) => taskSum + task.total_amount, 0),
            0
          )
        : 0)
    : 0;

  const transformedQuote = transformHubContextToAcceptedQuote(hub);

  if (!acceptedSubmission) {
    return null;
  }

  return (
    <>
      <div>
        <div className="mb-2 font-semibold md:ml-2">
          {t('acceptedOffer', { ns: 'hub' }).toLocaleUpperCase()}
        </div>
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-300 shadow-xl">
          <CollapsibleContent header={<div className=""></div>} expandButtonClassName="self-start">
            <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
              {/* LEFT SIDE */}
              <div className="flex-1">
                <h2 className="text-2xl font-semibold text-secondary mb-6">
                  {t('principal_freelancer', { ns: 'hub' })}
                </h2>

                <div className="flex items-center gap-6 pb-4">
                  <ProfileAvatar
                    profile_id={acceptedSubmission.freelancer.id}
                    first_name={acceptedSubmission.freelancer.first_name}
                    avatar_url={acceptedSubmission.freelancer.avatar_url}
                    className="w-24 h-24 text-3xl"
                  />

                  <div className="mt-6">
                    <div className="flex flex-row items-start justify-between gap-2 space-x-10">
                      <div>
                        <p className="text-lg font-semibold text-secondary">
                          {acceptedSubmission.freelancer.first_name}
                        </p>
                        <p className="text-secondary/80 text-sm">
                          {acceptedSubmission.freelancer.last_name}
                        </p>
                      </div>

                      <div className="text-left">
                        <p className="text-sm text-secondary/80">{t('offer', { ns: 'hub' })}</p>
                        <p className="text-lg font-semibold text-secondary">
                          <FormattedCurrency value={acceptedSubmission.totalAmount} />
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-secondary">
                    {t('team', { ns: 'hub' })}
                  </h2>
                </div>

                {/* Team */}
                {acceptedSubmission.teamMembers && (
                  <div className="divide-y divide-gray-400 pr-8">
                    {Array.from({
                      length: Math.ceil(acceptedSubmission.teamMembers!.length / 2),
                    }).map((_, rowIndex) => {
                      const first = acceptedSubmission.teamMembers![rowIndex * 2];
                      const second = acceptedSubmission.teamMembers![rowIndex * 2 + 1];

                      return (
                        <div key={rowIndex} className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-6">
                          {/* Member 1 */}
                          {first && (
                            <div className="flex items-center gap-4 pr-8">
                              <ProfileAvatar
                                profile_id={first.freelancer.id}
                                first_name={first.freelancer.first_name}
                                avatar_url={first.freelancer.avatar_url}
                                className="w-14 h-14 text-lg"
                              />

                              <div className="flex-1">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                                  <div>
                                    <p className="font-semibold text-secondary">
                                      {first.freelancer.first_name}
                                    </p>
                                    <p className="text-sm text-secondary/80">
                                      {first.freelancer.last_name}
                                    </p>
                                  </div>

                                  <div className="text-left sm:text-right">
                                    <p className="text-sm text-left text-secondary/80">
                                      {t('offer', { ns: 'hub' })}
                                    </p>
                                    <p className="font-semibold text-secondary">
                                      <FormattedCurrency
                                        value={first.tasks.reduce(
                                          (sum, task) => sum + task.total_amount,
                                          0
                                        )}
                                      />
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Member 2 */}
                          {second && (
                            <div className="flex items-center gap-4 md:pl-6">
                              <ProfileAvatar
                                profile_id={second.freelancer.id}
                                first_name={second.freelancer.first_name}
                                avatar_url={second.freelancer.avatar_url}
                                className="w-14 h-14 text-lg"
                              />

                              <div className="flex-1">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                                  <div>
                                    <p className="font-semibold text-secondary">
                                      {second.freelancer.first_name}
                                    </p>
                                    <p className="text-sm text-secondary/80">
                                      {second.freelancer.last_name}
                                    </p>
                                  </div>

                                  <div className="text-left sm:text-right">
                                    <p className="text-sm text-left text-secondary/80">
                                      {t('offer', { ns: 'hub' })}
                                    </p>
                                    <p className="font-semibold text-secondary">
                                      <FormattedCurrency
                                        value={second.tasks.reduce(
                                          (sum, task) => sum + task.total_amount,
                                          0
                                        )}
                                      />
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="md:my-8"></div>
              </div>
            </div>
          </CollapsibleContent>

          <div className="md:mt-4 pt-2">
            <div className="md:flex md:flex-row flex flex-col items-center justify-between gap-4">
              <div className="text-center">
                <p className="text-2xl font-semibold text-secondary">{t('total', { ns: 'hub' })}</p>
                <p className="text-4xl font-bold text-primary mt-2 ml-2">
                  <FormattedCurrency value={grandTotal} />
                </p>
              </div>

              <div className="w-full md:w-auto flex justify-center">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="bg-primary hover:bg-primary/90 transition-colors text-white px-8 py-2 rounded-full font-medium"
                >
                  {t('see_details', { ns: 'hub' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDetails && (
        <BidderDetailsModal
          onClose={() => setShowDetails(false)}
          job={hub.job}
          quote={transformedQuote}
        />
      )}
    </>
  );
};
